import React, { useState, useEffect } from 'react';
import passwordValidator from 'password-validator';
import { BiCheckboxChecked, BiCheckbox } from 'react-icons/bi';
import { INK, STATUS } from 'Utils/styleTokens';

function parseJwt(token) {
    var base64Url = token.split('.')[1];
    var base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    var jsonPayload = decodeURIComponent(window.atob(base64).split('').map(function (c) {
        return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
    }).join(''));

    return JSON.parse(jsonPayload);
}


// Shared password policy for every set-password flow (first-admin initialize, email reset
// link, forced change). Single source of truth — import this instead of re-declaring the
// password-validator schema per screen.
const buildPasswordSchema = () => {
    const schema = new passwordValidator();

    schema
        .is().min(8)
        .is().max(100)
        .has().uppercase()
        .has().lowercase()
        .has().digits()
        .has().symbols();

    return schema;
};

// Evaluates a candidate password against the shared schema. Returns
// { hasMin, hasUpper, hasLower, hasDigits, hasSymbols, valid }.
const checkPasswordRequirements = (schema, password) => {
    const failed = schema.validate(password || '', { list: true }).reduce((map, item) => {
        map[item] = true;
        return map;
    }, {});

    const result = {
        hasMin: !failed['min'],
        hasUpper: !failed['uppercase'],
        hasLower: !failed['lowercase'],
        hasDigits: !failed['digits'],
        hasSymbols: !failed['symbols'],
    };
    result.valid = result.hasMin && result.hasUpper && result.hasLower
        && result.hasDigits && result.hasSymbols;
    return result;
};

const PasswordRequirementsBox = (props) => {
    const [schema, setSchema] = useState(null);

    // validator states.
    const [hasMin, setHasMin] = useState(false);
    const [hasUpper, setHasUpper] = useState(false);
    const [hasLower, setHasLower] = useState(false);
    const [hasDigits, setHasDigits] = useState(false);
    const [hasSymbols, setHasSymbols] = useState(false);

    // Only meaningful when a confirm field is provided (e.g. first-admin creation).
    const showMatch = props.confirmInput !== undefined;
    const passwordsMatch = showMatch
        && props.passwordInput !== ''
        && props.passwordInput === props.confirmInput;

    useEffect(() => {
        setSchema(buildPasswordSchema());
    }, []);

    useEffect(() => {
        if (schema) {
            const reqs = checkPasswordRequirements(schema, props.passwordInput);
            setHasMin(reqs.hasMin);
            setHasUpper(reqs.hasUpper);
            setHasLower(reqs.hasLower);
            setHasDigits(reqs.hasDigits);
            setHasSymbols(reqs.hasSymbols);
        }
    }, [props.passwordInput]);

    return (
        <div style={{
            position: 'absolute',
            left: '-360px',
            top: '50px',
            background: '#fff',
            borderRadius: '5px',
            padding: '15px',
            width: '300px'
        }}>
            <PasswordValidationCheck validationKey='hasMin' hasMin={hasMin} validationText='8 characters minimum.' />
            <PasswordValidationCheck validationKey='digits' digits={hasDigits} validationText='1 number.' />
            <PasswordValidationCheck validationKey='symbols' symbols={hasSymbols} validationText='1 special character.' />
            <PasswordValidationCheck validationKey='uppercase' uppercase={hasUpper} validationText='1 uppercase letter.' />
            <PasswordValidationCheck validationKey='lowercase' lowercase={hasLower} validationText='1 lowercase letter.' />
            {showMatch &&
                <PasswordValidationCheck validationKey='match' match={passwordsMatch} validationText='Passwords match.' />
            }
            <div className="arrow-right" style={{
                position: 'absolute',
                right: '-30px',
                top: '50%',
                transform: 'translateY(-50%)',
                width: 0,
                height: 0,
                borderTop: '20px solid transparent',
                borderLeft: '30px solid #fff',
                borderBottom: '20px solid transparent',
            }}></div>
        </div>
    )
}

const PasswordValidationCheck = (props) => {
    const style = {
        color: props[props.validationKey] ? STATUS.success.dot : INK[400],
        fontFamily: 'Rubik, sans-serif',
        display: 'flex',
        padding: '0.5rem 0 0.5rem 0',
        alignItems: 'center'
    }

    return (
        <div
            style={style}
        >
            {props[props.validationKey] ?
                <BiCheckboxChecked style={{ marginRight: '0.5rem', fontSize: '1.5rem' }} /> :
                <BiCheckbox style={{ marginRight: '0.5rem', fontSize: '1.5rem' }} />
            }
            <span>{props.validationText}</span>
        </div>
    )
}

export { parseJwt, PasswordRequirementsBox, PasswordValidationCheck, buildPasswordSchema, checkPasswordRequirements }